import { onScopeDispose, toValue, watch, type MaybeRefOrGetter, type WatchStopHandle } from 'vue';
import type { ClientNotificationTab, ProjectNotificationTab } from '~/lib/notifications';
import type { NotificationContext } from '~/stores/notifications';
import type { NotificationEntityType } from '~/types/models';

export function useNotificationReadAcknowledger() {
    const notificationStore = useNotificationStore();
    const pendingStops = new Set<WatchStopHandle>();

    function runWhenReady(action: () => Promise<void>) {
        const run = async () => {
            try {
                await action();
            } catch (error) {
                console.warn('Unable to acknowledge viewed notifications:', error);
            }
        };

        if (notificationStore.startedForUserId && !notificationStore.loading) {
            void run();
            return;
        }

        const stop = watch(
            () => [notificationStore.startedForUserId, notificationStore.loading] as const,
            ([startedForUserId, loading]) => {
                if (!startedForUserId || loading) return;
                stop();
                pendingStops.delete(stop);
                void run();
            },
        );
        pendingStops.add(stop);
    }

    function acknowledgeEntity(
        entityType: NotificationEntityType,
        entityId: string,
        context: MaybeRefOrGetter<NotificationContext>,
    ) {
        if (!entityId) return;
        runWhenReady(() => notificationStore.markEntityAsRead(entityType, entityId, toValue(context)));
    }

    function acknowledgeEntities(
        entityType: NotificationEntityType,
        entityIds: string[],
        context: MaybeRefOrGetter<NotificationContext>,
    ) {
        const ids = entityIds.filter(Boolean);
        if (!ids.length) return;
        runWhenReady(() => notificationStore.markEntitiesAsRead(entityType, ids, toValue(context)));
    }

    function acknowledgeClientTab(
        tab: ClientNotificationTab,
        context: MaybeRefOrGetter<NotificationContext>,
    ) {
        runWhenReady(() => notificationStore.markClientTabAsRead(tab, toValue(context)));
    }

    function acknowledgeProjectTab(
        tab: ProjectNotificationTab,
        context: MaybeRefOrGetter<NotificationContext>,
    ) {
        runWhenReady(() => notificationStore.markProjectTabAsRead(tab, toValue(context)));
    }

    onScopeDispose(() => {
        pendingStops.forEach(stop => stop());
        pendingStops.clear();
    });

    return {
        acknowledgeEntity,
        acknowledgeEntities,
        acknowledgeClientTab,
        acknowledgeProjectTab,
    };
}
